/* eslint-disable max-len */
import { useState } from 'react'

/**
 * Custom hook for handling clipboard functionality.
 *
 * @param {Object} options - Options for the hook.
 * @param {number} options.timeout - Timeout duration in milliseconds for resetting the copied state. Default is 2000ms.
 *
 * @returns {Object} - An object containing the following properties:
 *   - copy: A function that copies the provided value to the clipboard.
 *   - reset: A function that resets the copied state and error.
 *   - error: An Error object representing any error that occurred during copying.
 *   - copied: A boolean indicating whether the value has been successfully copied to the clipboard. Will be reset after the timeout duration.
 */
export default function useClipboard({ timeout = 2000 } = {}) {
	const [error, setError] = useState<Error | null>(null)
	const [copied, setCopied] = useState(false)
	const [copyTimeout, setCopyTimeout] = useState<number | null>(null)

	const handleCopyResult = (value: boolean) => {
		if (copyTimeout) window.clearTimeout(copyTimeout)
		setCopyTimeout(window.setTimeout(() => setCopied(false), timeout))
		setCopied(value)
	}

	const copy = (valueToCopy: any) => {
		if ('clipboard' in navigator) {
			navigator.clipboard
				.writeText(valueToCopy)
				.then(() => handleCopyResult(true))
				.catch((err) => setError(err))
		} else {
			setError(new Error('useClipboard: navigator.clipboard is not supported'))
		}
	}

	const reset = () => {
		setCopied(false)
		setError(null)
		if (copyTimeout) window.clearTimeout(copyTimeout)
	}

	return { copy, reset, error, copied }
}
